import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, Target, TrendingUp, Search, Palette, Globe, 
  ArrowRight, Mail, MapPin, Phone, ChevronDown, 
  BarChart3, MousePointer2, Sun, Moon, ArrowLeft,
  Clock, BookOpen, Share2, Menu, X
} from 'lucide-react';

// --- Constants ---
const WHATSAPP_URL = "https://wa.me/918240266320";

// --- High-Stability "Refractive Core" ---
const RefractiveCore = ({ isDark }) => {
  return (
    <div className="relative w-72 h-72 md:w-[500px] md:h-[500px] flex items-center justify-center">
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute inset-0 bg-cyan-500 rounded-full blur-[120px]"
      />
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }} className="relative w-64 h-64 md:w-80 md:h-80">
        <div className={`absolute inset-0 border rounded-[2rem] opacity-20 ${isDark ? 'border-white' : 'border-black'}`} />
        <motion.div 
          animate={{ rotate: -360 }} transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className={`absolute inset-4 border backdrop-blur-xl rounded-[1.5rem] ${isDark ? 'border-white/10 bg-white/5' : 'border-black/5 bg-black/[0.02]'}`}
          style={{ boxShadow: isDark ? 'inset 0 0 40px rgba(255,255,255,0.05)' : 'inset 0 0 40px rgba(0,0,0,0.02)' }}
        />
        <motion.div animate={{ rotate: 180 }} transition={{ duration: 15, repeat: Infinity, ease: "linear" }} className="absolute inset-12 border border-cyan-500/30 backdrop-blur-md rounded-lg flex items-center justify-center">
          <div className="w-1 h-12 bg-cyan-500 opacity-50 blur-sm" />
        </motion.div>
      </motion.div>
    </div>
  );
};

// --- Structural Service Component (Updated Item Layout) ---
const ServicePanel = ({ icon: Icon, title, items, index, isDark }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.8 }}
      className={`group relative py-12 md:py-16 border-t transition-all duration-700 overflow-hidden ${
        isDark ? 'border-white/10 hover:bg-white/[0.02]' : 'border-black/10 hover:bg-black/[0.01]'
      }`}
    >
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-cyan-500/0 via-cyan-500/[0.03] to-cyan-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
      
      <div className="max-w-[1600px] mx-auto px-8 md:px-20 grid grid-cols-1 lg:grid-cols-12 items-center gap-8 md:gap-12 relative z-10 text-left">
        {/* Index & Icon */}
        <div className="lg:col-span-1 flex items-center gap-6">
          <span className="text-[10px] font-black font-header opacity-20 tracking-tighter">0{index + 1}</span>
          <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center text-cyan-500 group-hover:scale-125 transition-transform duration-500">
            <Icon size={28} strokeWidth={1.5} />
          </div>
        </div>

        {/* Title */}
        <div className="lg:col-span-4">
          <h3 className={`text-3xl md:text-5xl font-light tracking-tighter font-header transition-colors duration-500 ${isDark ? 'text-white' : 'text-black'} group-hover:text-cyan-500`}>{title}</h3>
        </div>

        {/* Capability List */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          {items.map((item, idx) => (
            <div key={idx} className="flex items-center gap-4 group/item">
              <div className="w-1 h-1 bg-cyan-500/40 group-hover/item:bg-cyan-500 transition-colors duration-300" />
              <span className={`text-[10px] md:text-[11px] font-bold uppercase tracking-[0.35em] transition-colors duration-700 ${isDark ? 'text-gray-500 group-hover:text-gray-300' : 'text-gray-400 group-hover:text-gray-600'}`}>
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* Action */}
        <div className="lg:col-span-2 flex justify-start lg:justify-end">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-4 px-8 py-4 border text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-500 ${isDark ? 'border-white/10 hover:border-cyan-500 hover:bg-cyan-500 hover:text-black' : 'border-black/10 hover:border-cyan-500 hover:bg-cyan-500 hover:text-black'}`}>Initiate <ArrowRight size={14} /></a>
        </div>
      </div>
    </motion.div>
  );
};

// --- Blog Content Data ---
const BLOG_POSTS = [
  {
    id: 1,
    category: "AI in Marketing",
    title: "The Generative Shift: Redefining Marketing ROI through Neural Logic",
    excerpt: "In 2026, content is no longer a manual craft. Discover how Generative AI architectures are refracting traditional marketing budgets into high-velocity revenue engines.",
    date: "March 12, 2026",
    readTime: "6 min read",
    content: `<h2>The Death of the Manual Funnel</h2><p>Traditional marketing funnels are leaking attention. With AI-driven neural logic, we are moving from static campaigns to dynamic, self-correcting ecosystems. By utilizing Large Language Models (LLMs) and predictive analytics, brands can now anticipate consumer friction before it happens.</p>`
  },
  {
    id: 2,
    category: "Automation",
    title: "Precision Automation: Building Self-Optimizing Ad Ecosystems",
    excerpt: "Automation is no longer about scheduling posts. It's about building a digital nervous system that reacts to market volatility in milliseconds.",
    date: "March 08, 2026",
    readTime: "8 min read",
    content: `<h2>Beyond Task Management</h2><p>Automation in digital marketing has evolved from simple 'if-this-then-that' rules into complex cognitive workflows. Modern automation layers act as a digital nervous system, adjusting bidding strategies and creative rotations based on real-time data signals.</p>`
  },
  {
    id: 3,
    category: "Digital Trends",
    title: "Digital Marketing Advancement: The Rise of Refractive UI",
    excerpt: "Why flat design is failing to convert and how immersive, refractive interfaces are capturing the next generation of high-net-worth attention.",
    date: "March 01, 2026",
    readTime: "5 min read",
    content: `<h2>The Boredom of the Flat Web</h2><p>Users are suffering from 'interface fatigue'. Template-based designs have made the internet look identical. To stand out, brands are adopting Refractive UI—interfaces that use depth and glassmorphism to create a sense of tactile luxury.</p>`
  }
];

const App = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isDark, setIsDark] = useState(true);
  const [view, setView] = useState('home'); 
  const [selectedPost, setSelectedPost] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.height = 'auto';
    }
  }, [mobileMenuOpen]);

  const themeClasses = isDark ? "bg-[#0A0A0B] text-white" : "bg-[#F5F5F7] text-black";

  const services = [
    { title: 'Strategy', icon: Target, items: ['Brand Positioning', 'Marketing Roadmaps', 'Funnel Planning'] },
    { title: 'Social', icon: Zap, items: ['Scroll-stopping content', 'Reels', 'Influencer Collabs'] },
    { title: 'Performance', icon: TrendingUp, items: ['Meta/Google/LinkedIn Ads', 'Retargeting', 'Lead Gen'] },
    { title: 'SEO', icon: Search, items: ['Technical & Local SEO', 'Keyword Strategy'] },
    { title: 'Creative', icon: Palette, items: ['Motion Graphics', 'Ad Creatives', 'Brand Identity'] },
    { title: 'Web', icon: Globe, items: ['Shopify', 'WordPress', 'CRO Optimization'] },
  ];

  const handleNav = (v, id) => {
    setView(v);
    setSelectedPost(null);
    setMobileMenuOpen(false);
    if (v === 'home' && id) {
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({behavior:'smooth'});
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const Navigation = () => (
    <nav className={`fixed top-0 w-full z-[100] transition-all duration-700 px-6 md:px-8 py-6 md:py-8 ${scrolled ? (isDark ? 'bg-black border-b border-white/5 py-4 shadow-2xl' : 'bg-white border-b border-black/5 py-4 shadow-sm') : ''}`}>
      <div className="max-w-[1600px] mx-auto flex justify-between items-center font-header relative z-[110]">
        <button onClick={() => handleNav('home')} className="flex items-center gap-4 group cursor-pointer text-left">
          <div className="relative w-6 h-6">
            <div className={`absolute inset-0 border rotate-45 group-hover:rotate-90 transition-all duration-500 ${isDark ? 'border-white/40' : 'border-black/30'}`} />
            <div className="absolute inset-1.5 bg-cyan-500" />
          </div>
          <span className="text-sm font-extrabold tracking-[0.4em] uppercase">Creative Prism</span>
        </button>

        <div className="hidden lg:flex gap-10 items-center">
          {/* REMOVED Strategy from here */}
          {['Services', 'Blog', 'Contact'].map(item => (
            <button key={item} onClick={() => handleNav(item === 'Blog' ? 'blog' : 'home', item.toLowerCase())} className={`text-[10px] font-bold uppercase tracking-[0.35em] transition-colors ${view === (item.toLowerCase()) ? 'text-cyan-500' : (isDark ? 'text-gray-400 hover:text-cyan-400' : 'text-gray-500 hover:text-cyan-500')}`}>{item}</button>
          ))}
          <div className={`h-4 w-[1px] mx-2 transition-colors ${isDark ? 'bg-white/10' : 'bg-black/10'}`} />
          <button onClick={() => setIsDark(!isDark)} className={`p-2 rounded-full ${isDark ? 'bg-white/5 text-white' : 'bg-black/5 text-black'}`}>{isDark ? <Sun size={14} /> : <Moon size={14} />}</button>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={`px-8 py-2.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${isDark ? 'border border-white/20 hover:bg-white hover:text-black' : 'border border-black/20 hover:bg-black hover:text-white'}`}>Initiate Growth</a>
        </div>

        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-cyan-500 focus:outline-none" aria-label="Toggle Menu">
          {mobileMenuOpen ? <X size={28} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.4, ease: "circOut" }}
            className={`fixed inset-0 z-[105] lg:hidden flex flex-col ${isDark ? 'bg-[#0A0A0B]' : 'bg-[#F5F5F7]'}`}
          >
            <div className="flex flex-col h-full p-8 pt-32 gap-8 font-header">
              {/* REMOVED Strategy from here */}
              {['Services', 'Blog', 'Contact'].map((item, idx) => (
                <button 
                  key={item} 
                  onClick={() => handleNav(item === 'Blog' ? 'blog' : 'home', item.toLowerCase())}
                  className={`text-5xl font-black tracking-tighter text-left hover:text-cyan-500 transition-colors uppercase italic flex items-center gap-4 ${isDark ? 'text-white' : 'text-black'}`}
                >
                  <span className="text-[10px] font-mono opacity-20 tracking-widest not-italic">0{idx + 1}</span>
                  {item}
                </button>
              ))}
              <div className="mt-auto flex flex-col gap-10 pb-16">
                <div className={`h-[1px] w-full ${isDark ? 'bg-white/10' : 'bg-black/10'}`} />
                <button onClick={() => setIsDark(!isDark)} className={`flex items-center gap-6 text-[10px] font-black uppercase tracking-[0.4em] ${isDark ? 'text-white' : 'text-black'}`}>
                  <div className="w-10 h-10 rounded-full border border-current/10 flex items-center justify-center">
                    {isDark ? <Sun size={16} /> : <Moon size={16} />}
                  </div>
                  Toggle {isDark ? 'Light' : 'Dark'} Mode
                </button>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="bg-cyan-500 text-black p-6 text-center font-black uppercase tracking-[0.3em] text-xs shadow-2xl shadow-cyan-500/20">Initiate Growth</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );

  const BlogPage = () => (
    <div className="pt-40 pb-20 px-8 max-w-[1200px] mx-auto min-h-screen">
      <AnimatePresence mode="wait">
        {!selectedPost ? (
          <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} exit={{opacity:0, y:-20}} key="list">
            <div className="mb-20">
              <span className="text-cyan-500 font-bold uppercase tracking-[0.5em] text-[10px] mb-4 block font-header text-left">Insights Hub</span>
              <h1 className="text-5xl md:text-8xl font-black font-header tracking-tighter mb-8 leading-none text-left">THE PHYSICS <br/>OF AI MARKETING.</h1>
              <p className={`text-xl max-w-2xl font-medium leading-relaxed text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Curated intelligence for the next era of high-growth digital dominance.</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {BLOG_POSTS.map(post => (
                <div key={post.id} onClick={() => {setSelectedPost(post); window.scrollTo(0,0);}} className={`group p-8 border transition-all cursor-pointer ${isDark ? 'border-white/10 bg-white/2 hover:border-cyan-500/50' : 'border-black/5 bg-black/[0.01] hover:border-cyan-500'}`}>
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-500 text-black px-3 py-1">{post.category}</span>
                    <span className="text-[10px] font-bold text-gray-500">{post.readTime}</span>
                  </div>
                  <h3 className={`text-2xl font-bold font-header leading-tight mb-6 group-hover:text-cyan-500 transition-colors text-left`}>{post.title}</h3>
                  <p className={`text-sm leading-relaxed mb-8 line-clamp-3 text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{post.excerpt}</p>
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-cyan-500">Explore Depth <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" /></div>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div initial={{opacity:0, x:20}} animate={{opacity:1, x:0}} exit={{opacity:0, x:-20}} key="post" className="max-w-3xl mx-auto">
            <button onClick={() => setSelectedPost(null)} className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-cyan-500 mb-12 hover:gap-4 transition-all"><ArrowLeft size={14} /> Back to Hub</button>
            <div className="mb-12">
              <div className="flex items-center gap-6 mb-8 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                <span className="flex items-center gap-1 text-left"><Clock size={12}/> {selectedPost.date}</span>
                <span className="flex items-center gap-1 text-left"><BookOpen size={12}/> {selectedPost.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-black font-header tracking-tighter mb-8 leading-tight text-left">{selectedPost.title}</h1>
              <div className={`h-1 w-20 bg-cyan-500 mb-12`} />
            </div>
            <article className={`prose prose-invert max-w-none mb-20 blog-article-content text-left ${isDark ? 'text-gray-300' : 'text-gray-600'}`} dangerouslySetInnerHTML={{__html: selectedPost.content}} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className={`${themeClasses} font-body min-h-screen transition-colors duration-700 ease-in-out overflow-x-hidden scroll-smooth selection:bg-cyan-500 selection:text-black`}>
      <Navigation />
      
      {view === 'home' ? (
        <main>
          {/* Hero */}
          <section className="relative min-h-screen flex items-center px-8 md:px-20 overflow-hidden pt-20">
            <div className="max-w-[1600px] mx-auto w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div className="relative z-10 w-full lg:col-span-2 pb-32 text-left">
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
                  <div className="flex items-center gap-6 mb-10"><div className="w-16 h-[1px] bg-cyan-500" /><span className="text-cyan-500 text-[11px] font-bold uppercase tracking-[0.6em] font-header">AI-Engineered Performance</span></div>
                  <h1 className="text-5xl md:text-[110px] font-light leading-[0.85] mb-20 tracking-tighter font-header">BUILD <br /><span className={`font-black italic text-transparent transition-all duration-700 select-none ${isDark ? 'border-text-dark' : 'border-text-light'}`}>ATTENTION.</span><br />DRIVE <span className="font-bold text-cyan-500">VALUE.</span></h1>
                  <div className="flex flex-col lg:flex-row gap-12 justify-between items-start lg:items-end w-full">
                    <p className={`max-w-lg text-base md:text-xl font-medium leading-[1.6] border-l-2 pl-10 transition-colors duration-700 tracking-tight ${isDark ? 'text-white/90 border-cyan-500/50' : 'text-black/80 border-cyan-500'}`}>We don't post content. We architect systems of attention that convert human curiosity into measurable enterprise revenue.</p>
                    <div className="flex items-center pb-8 font-header"><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className={`group relative flex items-center justify-center px-14 py-7 font-black text-[11px] uppercase tracking-[0.3em] transition-all duration-500 shadow-2xl ${isDark ? 'bg-white text-black hover:bg-cyan-400' : 'bg-black text-white hover:bg-cyan-500 shadow-cyan-500/20'}`}><span className="relative z-10 flex items-center gap-5">Get The Roadmap <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform" /></span></a></div>
                  </div>
                </motion.div>
              </div>
              <div className="hidden lg:flex justify-center items-center absolute right-20 top-1/2 -translate-y-1/2 pointer-events-none opacity-40"><RefractiveCore isDark={isDark} /></div>
            </div>
          </section>

          {/* Stats Section */}
          <section className={`py-32 border-t border-b transition-colors duration-700 ${isDark ? 'bg-current/[0.02] border-white/5' : 'bg-black/[0.01] border-black/5'}`}>
            <div className="max-w-[1600px] mx-auto px-8 md:px-20">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-16">
                {[
                  { label: 'Project Complete', val: '173+' },
                  { label: 'Client Relations', val: '472+' },
                  { label: 'Multi-Channel Expertise', val: '235+' },
                  { label: 'Best Customer Service', val: '250+' }
                ].map((stat, i) => (
                  <div key={i} className="flex flex-col text-left border-l-2 border-cyan-500/30 pl-8 group">
                    <span className="text-5xl md:text-7xl font-black font-header tracking-tighter mb-4 group-hover:text-cyan-500 transition-colors duration-500">{stat.val}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.4em] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* SERVICES SECTION */}
          <section id="services" className="py-40 bg-current/[0.01]">
            <div className="max-w-[1600px] mx-auto px-8 md:px-20 mb-32">
              <div id="strategy" className="lg:w-2/3 font-header text-left">
                <span className="text-cyan-500 font-bold uppercase tracking-[0.5em] text-[10px] mb-8 block">Core Capabilities</span>
                <h2 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.9] mb-12">THE PHYSICS <br/> OF CONVERSION.</h2>
                <div className="flex flex-col md:flex-row gap-16 md:items-end">
                  <p className="text-lg md:text-xl opacity-60 leading-relaxed max-w-xl">Creative Prism transforms brand architecture into liquid revenue through six high-performance pillars. Every pixel is calculated for growth.</p>
                  <button onClick={() => handleNav('blog')} className="text-[10px] font-black uppercase tracking-[0.3em] flex items-center gap-4 hover:gap-6 transition-all text-cyan-500 group whitespace-nowrap">Explore Insights <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform"/></button>
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              {services.map((s, i) => <ServicePanel key={i} {...s} index={i} isDark={isDark} />)}
            </div>
          </section>
        </main>
      ) : (
        <BlogPage />
      )}

      {/* Industrial Footer */}
      <footer id="contact" className={`pt-40 pb-20 px-8 transition-colors ${isDark ? 'bg-black border-t border-white/5' : 'bg-white border-t border-black/5'}`}>
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-20">
          <div className="lg:col-span-6 font-header text-left">
            <div className="text-4xl font-black mb-12 tracking-tighter">CREATIVE PRISM.</div>
            <div className="grid grid-cols-2 gap-12 text-[10px] font-bold uppercase tracking-[0.3em] text-gray-500">
               <div className="flex flex-col gap-5">
                 <span className={isDark ? 'text-white' : 'text-black'}>Strategy</span>
                 <button onClick={() => handleNav('home')} className="text-left hover:text-cyan-500 transition-colors">Attention Logic</button>
                 <button onClick={() => handleNav('home', 'services')} className="text-left hover:text-cyan-500 transition-colors">Revenue Maps</button>
               </div>
               <div className="flex flex-col gap-5">
                 <span className={isDark ? 'text-white' : 'text-black'}>Intelligence</span>
                 <button onClick={() => handleNav('blog')} className="text-left hover:text-cyan-500 transition-colors">AI Insights</button>
                 <button onClick={() => handleNav('home', 'contact')} className="text-left hover:text-cyan-500 transition-colors">Contact Hub</button>
               </div>
            </div>
          </div>
          <div className="lg:col-span-6 flex flex-col lg:items-end justify-between font-header text-left lg:text-right">
            <div className="space-y-8">
              <h4 className="text-[10px] font-bold uppercase tracking-[0.5em] text-cyan-500">Headquarters</h4>
              <div className={`space-y-6 text-sm font-bold transition-colors duration-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="flex gap-4 lg:justify-end items-start leading-relaxed uppercase tracking-widest"><MapPin size={18} className="text-cyan-500 shrink-0" /><span className="font-body normal-case tracking-normal">4th Floor, Room No.4WS3c, Newtown,<br/>Kolkata, WB 700160</span></div>
                <div className="flex gap-4 lg:justify-end items-center uppercase tracking-widest"><Mail size={18} className="text-cyan-500 shrink-0" /><span className="lowercase font-body tracking-normal">contact@creativeprism.in</span></div>
                <div className="flex gap-4 lg:justify-end items-center uppercase tracking-widest"><Phone size={18} className="text-cyan-500 shrink-0" /><span className="font-body tracking-normal">+91 824 026 6320</span></div>
              </div>
            </div>
            <div className="mt-20 flex flex-col lg:items-end gap-6 text-[10px] font-bold uppercase tracking-[0.5em] text-gray-600">
              <div className="flex gap-10">
                <a href="#" className="hover:text-cyan-500 transition-colors">Instagram</a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-500 transition-colors">LinkedIn</a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="hover:text-cyan-500 transition-colors">WhatsApp</a>
              </div>
              <span>© 2026 Creative Prism Architecture.</span>
            </div>
          </div>
        </div>
      </footer>

      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;700;800;900&family=Inter:wght@400;500;600;700&display=swap');
        html { scroll-behavior: smooth; }
        body { font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }
        .font-header { font-family: 'Outfit', sans-serif; }
        .font-body { font-family: 'Inter', sans-serif; }
        .border-text-dark { -webkit-text-stroke: 1.5px rgba(255,255,255,0.3); color: transparent; }
        .border-text-light { -webkit-text-stroke: 1.5px rgba(0,0,0,0.45); color: transparent; }
        .blog-article-content h2 { font-family: 'Outfit', sans-serif; font-size: 2rem; font-weight: 900; margin-bottom: 2rem; margin-top: 3rem; }
        .blog-article-content p { font-size: 1rem; line-height: 1.8; margin-bottom: 1.5rem; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #333; }
        ::-webkit-scrollbar-thumb:hover { background: #22d3ee; }
      `}} />
    </div>
  );
};

export default App;